import { FC, useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';
import { Backdrop, Button, CircularProgress, Modal } from '@mui/material';
import HostFormModal from './HostForm';
import SecondTypeButton from 'src/components/Items/button/SecondTypeButton';
import { useActions } from 'src/hooks/useActions';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import {
  validateEmail,
  validateField,
  validatePassword,
  validatePhone
} from 'src/common/utils/validation';
import { Host } from 'src/entity/Host';
import { IHostFetch } from 'src/models/IHost';
import './styles.scss';

interface IProps {
  open: boolean;
  handleClose: () => void;
  editHost?: IHostFetch;
}

export type IErrorsHostData = {
  email?: string | undefined;
  last_name?: string | undefined;
  first_name?: string | undefined;
  phone?: string | undefined;
  country?: string | undefined;
  city?: string | undefined;
  host_tag?: string | undefined;
  password?: string | undefined;
};

const HostModal: FC<IProps> = ({ open, handleClose, editHost }) => {
  const { addAvatar, updateHost, addHost } = useActions();
  const { addedHostId, isLoading, result } = useTypedSelector(state => state.host);
  const [host, setHost] = useState<Host | IHostFetch>(new Host());
  const [avatar, setAvatar] = useState<File | null>(null);
  const [disable, setDisable] = useState(true);
  const [check, setCheck] = useState(false);
  const [errors, setErrors] = useState<IErrorsHostData>({});

  const handleCloseModal = () => {
    editHost ? setHost(editHost) : setHost(new Host());
    setAvatar(null);
    setCheck(false);
    setErrors({});
    handleClose();
  };

  useEffect(() => {
    if (editHost) setHost(editHost);
  }, [editHost]);

  useEffect(() => {
    if (!avatar) {
      const updatedHost = host;
      updatedHost.password === '' && delete updatedHost.password;
      if (JSON.stringify(editHost) === JSON.stringify(updatedHost)) setDisable(true);
      else setDisable(false);
    } else setDisable(false);
  }, [host, avatar]);

  useEffect(() => {
    if (result.add === 201 && avatar) {
      addAvatar(addedHostId!, avatar);
      handleCloseModal();
    }
  }, [result.add]);

  const handleInput = (name: string) => (value: string | number | boolean) => {
    if (name === 'host_tag') {
      setHost({ ...host, host_tag: value.toString().toUpperCase() });
    } else if (name === 'email') {
      setHost({ ...host, email: value.toString().toLowerCase() });
    } else {
      setHost({ ...host, [name]: value });
    }
  };

  const validateInputs = (): boolean => {
    const first_name = editHost ? '' : validateField(host.first_name);
    const last_name = editHost ? '' : validateField(host.last_name);
    const email = editHost ? '' : validateEmail(host.email);
    const phone = editHost ? '' : validatePhone(String(host.phone));
    const host_tag = editHost ? '' : validateField(host.host_tag);
    const password = editHost ? '' : validatePassword(host.password!, host.email);
    setErrors({
      first_name,
      last_name,
      email,
      phone,
      host_tag,
      password
    });
    return [first_name, last_name, email, phone, host_tag, password].every(el => !el);
  };

  const handleSubmit = () => {
    setCheck(true);
    if (validateInputs()) {
      if (editHost) {
        const updatedHost = cloneDeep(host);
        for (const key in host) {
          (host[key as keyof typeof host] === editHost[key as keyof typeof editHost] ||
            (key !== 'stripe_connect_id' && host[key as keyof typeof host] === '')) &&
            delete updatedHost[key as keyof typeof updatedHost];
        }
        if (Object.keys(updatedHost).length) {
          updateHost(updatedHost as IHostFetch, editHost.id);
        }
        if (avatar) {
          addAvatar(editHost.id, avatar);
        }
        handleCloseModal();
      } else {
        addHost(host);
        if (!avatar) handleCloseModal();
      }
    }
  };

  return (
    <Modal open={open} onClose={handleCloseModal} BackdropComponent={Backdrop} closeAfterTransition>
      <div className='modal modal-850'>
        <div className='modal__title'>{editHost ? 'Edit Host' : 'Add Host'}</div>
        <HostFormModal
          host={host}
          setHost={handleInput}
          avatar={avatar}
          setAvatar={setAvatar}
          check={check}
          errors={errors}
        />
        <div className='col-2' style={{ marginTop: '30px' }}>
          <SecondTypeButton children='Cancel' onClick={handleCloseModal} />
          <Button
            variant='contained'
            onClick={handleSubmit}
            children={editHost ? 'Edit Host' : 'Add Host'}
            disabled={editHost ? disable : false}
          />
        </div>
        {isLoading.add && (
          <div className='host-modal__progress'>
            <CircularProgress size={100} />
          </div>
        )}
      </div>
    </Modal>
  );
};
export default HostModal;
